import { useMemo, useState, useRef, useEffect } from "react";
import {
  Typography,
  useDataGrid,
  DataGrid,
  List,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Box,
  Stack,
  Chip,
} from "@pankod/refine-mui";
import useLocales from "locales/useLocales";
import axios from "utils/axios";
import { toast } from "react-toastify";
import TranslateIcon from "@mui/icons-material/Translate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SkipNextIcon from "@mui/icons-material/SkipNext";

// Separate component for progress content to ensure proper re-renders
function TranslationProgressContent({ jobStatus, translating, onClose }) {
  console.log("TranslationProgressContent render:", {
    processed: jobStatus.processed,
    total: jobStatus.total,
    translated: jobStatus.translated,
    skipped: jobStatus.skipped,
    errors: jobStatus.errors,
  });

  const progressPercent =
    jobStatus && jobStatus.total > 0
      ? Math.round((jobStatus.processed / jobStatus.total) * 100)
      : 0;

  return (
    <Box sx={{ mt: 1 }}>
      {/* Phase indicator */}
      {translating && jobStatus.phase && (
        <Box sx={{ mb: 2 }}>
          <Chip
            size="small"
            color={
              jobStatus.phase === "cleanup"
                ? "warning"
                : jobStatus.phase === "writing"
                  ? "info"
                  : "primary"
            }
            label={
              jobStatus.phase === "cleanup"
                ? "Cleaning up orphans"
                : jobStatus.phase === "writing"
                  ? "Writing to DB"
                  : "Translating"
            }
          />
        </Box>
      )}

      {/* Overall Progress Bar */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Progress: {jobStatus.processed}/{jobStatus.total}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {progressPercent}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Current Entry */}
      {jobStatus.currentEntry && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, fontStyle: "italic" }}
        >
          Current: {jobStatus.currentEntry}
        </Typography>
      )}

      {/* Orphan Cleanup */}
      {jobStatus.orphansDeleted > 0 && (
        <Box sx={{ mb: 2 }}>
          <Chip
            size="small"
            color="warning"
            label={`${jobStatus.orphansDeleted} orphan entries deleted`}
          />
        </Box>
      )}

      {/* Per-Locale Breakdown */}
      {jobStatus.locales && jobStatus.locales.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Per-Language Progress
          </Typography>
          <Stack spacing={1}>
            {jobStatus.locales.map((locale) => {
              const stats = jobStatus.perLocale?.[locale] || {
                translated: 0,
                errors: 0,
                skipped: 0,
              };
              return (
                <Box
                  key={locale}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 60, fontWeight: "bold" }}
                  >
                    {locale}
                  </Typography>
                  <Chip
                    size="small"
                    icon={<CheckCircleIcon />}
                    label={stats.translated}
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    icon={<SkipNextIcon />}
                    label={stats.skipped}
                    color="default"
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    icon={<ErrorIcon />}
                    label={stats.errors}
                    color="error"
                    variant="outlined"
                  />
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Summary Stats */}
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Chip
          icon={<CheckCircleIcon />}
          label={`${jobStatus.translated} translated`}
          color="success"
        />
        <Chip
          icon={<SkipNextIcon />}
          label={`${jobStatus.skipped} skipped`}
          color="default"
        />
        <Chip
          icon={<ErrorIcon />}
          label={`${jobStatus.errors} errors`}
          color="error"
        />
      </Stack>

      {/* Close button when done */}
      {!translating && (
        <Box sx={{ mt: 3, textAlign: "right" }}>
          <Button variant="contained" color="primary" onClick={onClose}>
            Close
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default function CustomList({
  title,
  columns,
  resource,
  canCreate,
  translateContentType,
}) {
  const { currentLang } = useLocales();
  const columsMemo = useMemo(() => columns, []);
  const [translating, setTranslating] = useState(false);
  const [progress, setProgress] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);

  // Debug: log when jobStatus changes
  useEffect(() => {
    console.log("jobStatus changed:", jobStatus);
  }, [jobStatus]);

  // Clean up any pending poll timer when the component unmounts.
  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const { dataGridProps, sortingMode, sortModel, onSortModelChange } =
    useDataGrid({
      metaData: {
        locale: currentLang?.value?.includes("ru")
          ? currentLang.value
          : currentLang.value.substring(0, 2),
        populate: "*",
      },
    });

  const pollStatus = async (jobId) => {
    try {
      const { data: status } = await axios.get(
        `/api/translate-all-status?jobId=${jobId}&_t=${Date.now()}`,
      );

      console.log("Poll status update:", {
        processed: status.processed,
        total: status.total,
        translated: status.translated,
        skipped: status.skipped,
        errors: status.errors,
        phase: status.phase,
        perLocale: status.perLocale,
      });
      setJobStatus(status);

      if (status.status === "running") {
        setProgress({ processed: status.processed, total: status.total });
        pollRef.current = setTimeout(() => pollStatus(jobId), 1000);
        return;
      }

      setTranslating(false);
      setProgress(null);

      if (status.status === "done") {
        toast.success(
          `Translation complete: ${status.translated} translated, ${status.skipped} skipped, ${status.errors} errors${status.orphansDeleted ? `, ${status.orphansDeleted} orphans deleted` : ""}`,
        );
      } else {
        toast.error("Translation failed: " + (status.error || "unknown error"));
      }
    } catch (error) {
      setTranslating(false);
      setProgress(null);
      toast.error(
        "Translation status check failed: " +
          (error.message || "unknown error"),
      );
    }
  };

  const handleTranslateAll = async () => {
    if (!translateContentType) return;
    setTranslating(true);
    setProgress(null);
    setJobStatus(null);
    setModalOpen(true);
    try {
      const { data } = await axios.post("/api/translate-all", {
        contentType: translateContentType,
      });
      toast.info("Translation started in the background...");
      pollStatus(data.jobId);
    } catch (error) {
      setTranslating(false);
      setModalOpen(false);
      toast.error("Translation failed: " + (error.message || "unknown error"));
    }
  };

  const buttonLabel = translating
    ? progress && progress.total
      ? `Translating ${progress.processed}/${progress.total}...`
      : "Translating..."
    : "Translate All";

  const handleCloseModal = () => {
    if (translating) return; // don't close while running
    setModalOpen(false);
  };

  const progressPercent =
    jobStatus && jobStatus.total > 0
      ? Math.round((jobStatus.processed / jobStatus.total) * 100)
      : 0;

  return (
    <>
      <List
        resource={resource}
        createButtonProps={{ size: "small" }}
        canCreate={canCreate}
        title={<Typography variant="h5">{title}</Typography>}
        headerButtons={({ defaultButtons }) => (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {defaultButtons}
            {translateContentType && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<TranslateIcon />}
                disabled={translating}
                onClick={handleTranslateAll}
              >
                {buttonLabel}
              </Button>
            )}
          </div>
        )}
      >
        <DataGrid
          {...dataGridProps}
          columns={columsMemo}
          sortingMode={sortingMode}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          autoHeight
        />
      </List>

      {/* Translation Progress Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TranslateIcon color="primary" />
            <Typography variant="h6">
              {translating
                ? jobStatus?.phase === "cleanup"
                  ? "Cleaning up orphan entries..."
                  : jobStatus?.phase === "writing"
                    ? "Writing to database..."
                    : "Translating..."
                : "Translation Complete"}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {jobStatus && (
            <TranslationProgressContent
              jobStatus={jobStatus}
              translating={translating}
              onClose={handleCloseModal}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
